'use client';

import { useEffect, useState } from 'react';

import { cn } from '@/shared/lib/utils';

// CDN base URL for images (Cloudflare R2 with custom domain)
const CDN_BASE = 'https://r2.zestygen.com';

// Hero background images for image wall - served from CDN for better performance
const HERO_IMAGES = [
  { src: `${CDN_BASE}/hero/1.webp`, alt: 'AI Style Art' },
  { src: `${CDN_BASE}/hero/2.webp`, alt: 'AI Filter' },
  { src: `${CDN_BASE}/hero/3.webp`, alt: 'AI Pixar 3D Animation' },
  { src: `${CDN_BASE}/hero/4.webp`, alt: 'AI Art Effect' },
  { src: `${CDN_BASE}/hero/5.webp`, alt: 'AI Pixel Art Style' },
  { src: `${CDN_BASE}/hero/6.webp`, alt: 'AI Art Generator' },
  { src: `${CDN_BASE}/hero/7.webp`, alt: 'AI Image to Video' },
  { src: `${CDN_BASE}/hero/8.webp`, alt: 'AI Style Transfer Landscape' },
  { src: `${CDN_BASE}/hero/9.webp`, alt: 'AI Pet Photo Transformation' },
];

/**
 * Optimized Image Wall background.
 * - Client-only (avoids blocking SSR/LCP)
 * - Delays loading until idle (keeps main thread clear for LCP text paint)
 */
export function HeroImageWallBackground() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    // Skip image wall entirely on small screens (mobile LCP is much more sensitive)
    if (
      'matchMedia' in globalThis &&
      (globalThis as unknown as Window).matchMedia('(max-width: 768px)').matches
    ) {
      setIsDisabled(true);
      return;
    }

    // Delay image wall loading until after main content renders (improves LCP)
    if ('requestIdleCallback' in globalThis) {
      const w = globalThis as unknown as Window;
      const id = w.requestIdleCallback(() => setIsLoaded(true), {
        timeout: 5000,
      });
      return () => w.cancelIdleCallback(id);
    }

    const timer = setTimeout(() => setIsLoaded(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (isDisabled) {
    return null;
  }

  // Reduce image count - only 5 images per row, duplicated once for seamless loop
  const row1 = [...HERO_IMAGES.slice(0, 5), ...HERO_IMAGES.slice(0, 5)];
  const row2 = [...HERO_IMAGES.slice(4, 9), ...HERO_IMAGES.slice(4, 9)];
  const row3 = [...HERO_IMAGES.slice(2, 7), ...HERO_IMAGES.slice(2, 7)];

  return (
    <div
      className={cn(
        'absolute inset-0 -z-10 overflow-hidden transition-opacity duration-700',
        isLoaded ? 'opacity-100' : 'opacity-0'
      )}
      aria-hidden
    >
      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 z-20 bg-gradient-to-b from-background/10 via-background/20 to-background/20" />

      {/* Image grid container - rotated */}
      <div
        className="absolute -inset-[30%] z-10 flex flex-col justify-center gap-2"
        style={{ transform: 'rotate(-12deg)', willChange: 'transform' }}
      >
        {/* Row 1 - scroll left */}
        <div className={cn('flex gap-2', isLoaded ? 'animate-scroll-left' : '')}>
          {row1.map((image, idx) => (
            <div
              key={`r1-${idx}`}
              className="relative h-[200px] w-[200px] flex-shrink-0 overflow-hidden rounded-xl bg-muted md:h-[280px] md:w-[280px]"
            >
              {isLoaded && (
                <img
                  src={image.src}
                  alt={image.alt}
                  width={280}
                  height={280}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
              )}
            </div>
          ))}
        </div>

        {/* Row 2 - scroll right */}
        <div className={cn('flex gap-2', isLoaded ? 'animate-scroll-right' : '')}>
          {row2.map((image, idx) => (
            <div
              key={`r2-${idx}`}
              className="relative h-[200px] w-[200px] flex-shrink-0 overflow-hidden rounded-xl bg-muted md:h-[280px] md:w-[280px]"
            >
              {isLoaded && (
                <img
                  src={image.src}
                  alt={image.alt}
                  width={280}
                  height={280}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
              )}
            </div>
          ))}
        </div>

        {/* Row 3 - scroll left slower */}
        <div className={cn('flex gap-2', isLoaded ? 'animate-scroll-left-slow' : '')}>
          {row3.map((image, idx) => (
            <div
              key={`r3-${idx}`}
              className="relative h-[200px] w-[200px] flex-shrink-0 overflow-hidden rounded-xl bg-muted md:h-[280px] md:w-[280px]"
            >
              {isLoaded && (
                <img
                  src={image.src}
                  alt={image.alt}
                  width={280}
                  height={280}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Soft vignette effect */}
      <div className="absolute inset-0 z-30 bg-[radial-gradient(ellipse_at_center,transparent_0%,var(--background)_85%)]" />
    </div>
  );
}


